import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Typography, Empty, Spin } from 'antd';
import axios from 'axios';

const { Text } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const T = {
  ink: '#1f1f1f', sub: '#595959', muted: '#8c8c8c', faint: '#bfbfbf',
  border: '#d9d9d9', line: '#ececec', hairline: '#f0f0f0', panel: '#fafafa', white: '#ffffff',
  blue: '#3f74ad', blueSoft: '#eaf3fc', blueLine: '#d6e6f5',
};

const REFRESH_MS = 30 * 60 * 1000; // 30 minutes

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const RejectedInvoicesReminder = ({ username, isAccounts, onRevise }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const checkRejected = useCallback(async () => {
    if (isAccounts) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, {
        params: { submitted_by: username, status: 'rejected', limit: 50, offset: 0 },
      });
      const subs = res.data.submissions || [];
      setItems(subs);
      setCount(res.data.total || subs.length);
      if (subs.length > 0) setOpen(true);   // pop only when there's something
      else setOpen(false);
    } catch (err) {
      console.error('Rejected-invoice check failed:', err);
    } finally {
      setLoading(false);
    }
  }, [username, isAccounts]);

  // On login/mount + every 30 minutes
  useEffect(() => {
    if (isAccounts) return;
    checkRejected();
    timerRef.current = setInterval(checkRejected, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isAccounts, checkRejected]);

  if (isAccounts) return null;

  const handleRevise = () => {
    setOpen(false);
    if (onRevise) onRevise();
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
       width={Math.min(760, window.innerWidth * 0.92)}
      centered
      maskClosable
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 6, overflow: 'hidden' },
        mask: { background: 'rgba(0,0,0,0.45)' },
      }}
    >
      <div style={{ background: T.white }}>
        <div style={{ padding: '22px 28px 16px', borderBottom: `1px solid ${T.line}` }}>
          <div style={{ fontSize: 10.5, letterSpacing: 1.6, textTransform: 'uppercase', color: T.muted, fontWeight: 600, marginBottom: 8 }}>
            Action required
          </div>
          <div style={{ fontSize: 19, fontWeight: 700, color: T.ink, marginBottom: 6 }}>
            You have {count} rejected invoice{count !== 1 ? 's' : ''} to revise
          </div>
          <Text style={{ fontSize: 13, color: T.sub, lineHeight: 1.55 }}>
            Accounts have rejected the invoices below. Please check the remarks, correct the document,
            and submit a revised version under the same batch.
          </Text>
        </div>

        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: '8px 0' }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
          ) : items.length === 0 ? (
            <Empty style={{ padding: 30 }} description="No rejected invoices" />
          ) : (
            items.map((s) => (
              <div key={s.id} style={{ padding: '12px 28px', borderBottom: `1px solid ${T.hairline}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <Text strong style={{ fontSize: 14, color: T.ink, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.vendor_name || 'Unknown vendor'}
                    </Text>
                    <Text style={{ fontSize: 12, color: T.muted }}>
                      {s.submission_ref} · Batch {s.batch_no ?? '—'} · {formatAmount(s.total_amount)}
                    </Text>
                  </div>
                  <span style={{ flexShrink: 0, fontSize: 11, color: T.blue, border: `1px solid ${T.blueLine}`, background: T.blueSoft, borderRadius: 2, padding: '2px 8px', fontWeight: 600 }}>
                    Rejected
                  </span>
                </div>
                {s.accounts_remarks && (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: T.panel, border: `1px solid ${T.line}`, borderRadius: 4 }}>
                    <Text style={{ fontSize: 11.5, color: T.sub }}>
                      <span style={{ color: T.muted }}>Reason: </span>{s.accounts_remarks}
                    </Text>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '16px 28px', borderTop: `1px solid ${T.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={() => setOpen(false)} style={{ borderRadius: 4 }}>Remind me later</Button>
          <Button type="primary" onClick={handleRevise}
            style={{ background: T.blue, borderColor: T.blue, color: '#fff', borderRadius: 4, fontWeight: 600 }}>
            Go to Log Sheet to revise
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RejectedInvoicesReminder;